// src/components/settings/UserForm.js
import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Divider,
  Paper,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Autocomplete,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import { useAuth } from "../../hooks/useAuth";

const UserForm = ({ user, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { add, update } = useAuditFirestore("users");
  const { data: roles } = useCollection("roles");
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    email: user?.email || "",
    displayName: user?.displayName || "",
    role: user?.role || (roles && roles.length > 0 ? roles[0].id : ""),
    department: user?.department || "",
    phone: user?.phone || "",
    isActive: user?.isActive !== undefined ? user.isActive : true,
    uid: user?.uid || "", // Solo lectura, se genera automáticamente
  });

  // Actualizar formData cuando cambia el user
  useEffect(() => {
    setFormData({
      email: user?.email || "",
      displayName: user?.displayName || "",
      role: user?.role || (roles && roles.length > 0 ? roles[0].id : ""),
      department: user?.department || "",
      phone: user?.phone || "",
      isActive: user?.isActive !== undefined ? user.isActive : true,
      uid: user?.uid || "",
    });
  }, [user, roles]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError("El email es obligatorio");
      return;
    }

    if (!formData.role) {
      setError("Debes seleccionar un rol");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userData = {
        email: formData.email.trim(),
        displayName: formData.displayName.trim(),
        role: formData.role,
        department: formData.department.trim(),
        phone: formData.phone.trim(),
        isActive: formData.isActive,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid || "",
      };

      if (user?.id) {
        // Actualizar usuario existente
        await update(user.id, userData);
        console.log("✅ Usuario actualizado:", user.id);
      } else {
        // Crear nuevo usuario
        const newUserData = {
          ...userData,
          uid: userData.email.toLowerCase().replace(/[^a-z0-9]/g, "_") + "_" + Date.now(), // Generar uid único
          createdAt: new Date().toISOString(),
          lastLogin: null, // Nunca ha iniciado sesión
          createdBy: currentUser?.uid || "",
        };
        
        await add(newUserData);
        console.log("✅ Usuario creado:", newUserData.email);
      }
      
      onSave();
    } catch (err) {
      console.error("❌ Error:", err);
      setError(err.message || "Error al guardar usuario");
    } finally {
      setLoading(false);
    }
  };

  // Obtener el nombre del rol seleccionado
  const getSelectedRoleName = () => {
    if (!formData.role || !roles) return "";
    const role = roles.find(r => r.id === formData.role);
    return role?.name || formData.role;
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
        background: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
            {user?.id ? "Editar usuario" : "Nuevo usuario"}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
            {user?.id ? `Editando: ${user.email}` : "Completa los datos del nuevo usuario"}
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ 
            background: theme.palette.background.paper, 
            border: "1px solid",
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: "12px", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Typography sx={sectionTitleSx}>Información básica</Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2.5 } }}>
          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre completo"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              placeholder="Ej: José Pacheco"
            />
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email *"
              name="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
              required
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              placeholder="ej: jose.pacheco@epcsolar.es"
            />
          </Box>

          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
              required
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Rol *</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                label="Rol *"
                sx={{ 
                  borderRadius: "12px", 
                  background: theme.palette.background.paper, 
                  height: { xs: 40, sm: 44 }, 
                  fontSize: { xs: "0.8rem", sm: "0.875rem" } 
                }}
              >
                {roles && roles.length > 0 ? (
                  roles.map((role) => (
                    <MenuItem key={role.id} value={role.id} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                      {role.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                    No hay roles disponibles
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {formData.role && (
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: "250px" }, display: "flex", alignItems: "center" }}>
                <Chip 
                  label={`Rol seleccionado: ${getSelectedRoleName()}`}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    height: { xs: 40, sm: 44 },
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        <Typography sx={sectionTitleSx}>Información adicional</Typography>
        <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
          <TextField
            fullWidth
            size="small"
            label="Departamento"
            name="department"
            value={formData.department}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            placeholder="Ej: Sales, Engineering, etc."
          />
          <TextField
            fullWidth
            size="small"
            label="Teléfono"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            placeholder="Ej: 634 04 32 51"
          />
        </Box>

        {user?.id && (
          <>
            <Typography sx={sectionTitleSx}>Información del sistema (solo lectura)</Typography>
            <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
              <TextField
                fullWidth
                size="small"
                label="UID"
                value={formData.uid || ""}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Creado"
                value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString("es-ES") : "N/A"}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap", mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Último acceso"
                value={user?.lastLogin ? new Date(user.lastLogin).toLocaleString("es-ES") : "Nunca"}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <TextField
                fullWidth
                size="small"
                label="Actualizado"
                value={user?.updatedAt ? new Date(user.updatedAt).toLocaleString("es-ES") : "N/A"}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
                InputProps={{ readOnly: true }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
            </Box>
          </>
        )}

        <Typography sx={sectionTitleSx}>Estado</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleChange}
              name="isActive"
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
              {formData.isActive ? "Usuario activo" : "Usuario inactivo"}
            </Typography>
          }
        />

        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, boxShadow: "none", textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading ? "Guardando..." : user?.id ? "Actualizar usuario" : "Crear usuario"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default UserForm;